import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, RefreshCw, TrendingUp, Loader2, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { http } from "@/lib/api";

function Metric({ label, value, sub }) {
  return (
    <div className="border border-zinc-200 bg-white p-6">
      <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">{label}</p>
      <p className="mt-3 font-display text-5xl font-black tracking-tight text-zinc-950 tabular-nums">
        {value ?? "—"}
      </p>
      {sub && <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">{sub}</p>}
    </div>
  );
}

function Bar({ label, count, max }) {
  const pct = max > 0 ? (count / max) * 100 : 0;
  return (
    <div className="space-y-1.5">
      <div className="flex justify-between text-sm">
        <span className="text-zinc-800">{label}</span>
        <span className="font-mono tabular-nums text-zinc-500">{count}</span>
      </div>
      <div className="h-2 bg-zinc-100">
        <div className="h-full bg-rose-600 transition-all" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export default function StatsPage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [feedback, setFeedback] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const [s, f] = await Promise.all([
        http.get("/stats").then((r) => r.data),
        http.get("/feedback/list", { params: { limit: 50 } }).then((r) => r.data),
      ]);
      setStats(s);
      setFeedback(f);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const resetCounters = async () => {
    if (!window.confirm(
      "Reset the counters?\n\nThis marks all existing capsules and feedback as 'before now'. " +
      "No data is deleted — /api/stats will simply hide older rows from now on."
    )) return;
    try {
      const { data } = await http.post("/stats/reset");
      toast.success(`Counters reset · watching since ${new Date(data.stats_since).toLocaleString()}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed. Try again.");
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-zinc-50">
        <Loader2 className="h-6 w-6 animate-spin text-zinc-500" />
      </div>
    );
  }

  const maxMode = Math.max(1, ...Object.values(stats?.by_mode || {}));
  const maxDur = Math.max(1, ...Object.values(stats?.by_duration || {}));
  const maxTech = Math.max(1, ...(stats?.top_tech_topics || []).map((t) => t.count));

  return (
    <div className="min-h-screen bg-zinc-50">
      <header className="border-b border-zinc-950 bg-white">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 md:px-10">
          <button
            data-testid="stats-back"
            onClick={() => navigate("/")}
            className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.3em] text-zinc-600 hover:text-zinc-950"
          >
            <ArrowLeft className="h-4 w-4" /> Home
          </button>
          <div className="font-display text-xl font-black tracking-tight">
            <TrendingUp className="mr-2 inline h-5 w-5 text-rose-600" />
            Beta stats
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="stats-reset"
              onClick={resetCounters}
              className="inline-flex items-center gap-2 border border-rose-300 px-3 py-2 font-mono text-[11px] uppercase tracking-[0.25em] text-rose-600 hover:border-rose-600 hover:bg-rose-600 hover:text-white"
            >
              <RotateCcw className="h-3.5 w-3.5" />
              Reset counters
            </button>
            <button
              data-testid="stats-refresh"
              onClick={load}
              disabled={refreshing}
              className="inline-flex items-center gap-2 border border-zinc-300 px-3 py-2 font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-700 hover:border-zinc-950 hover:text-zinc-950 disabled:opacity-50"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} />
              Refresh
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 py-10 md:px-10">
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-rose-600">Private dashboard</p>
        <h1 className="mt-3 font-display text-4xl font-black tracking-tight md:text-5xl">
          What's happening in the newsroom
        </h1>
        <p className="mt-2 text-sm text-zinc-500">
          Refreshed {new Date(stats.generated_at).toLocaleString()} · v{stats.version}
          {stats.stats_since && (
            <span data-testid="stats-since-label" className="ml-2 text-rose-600">
              · watching since {new Date(stats.stats_since).toLocaleString()}
            </span>
          )}
        </p>

        <section className="mt-10 grid grid-cols-2 gap-4 md:grid-cols-4">
          <Metric label="Capsules today" value={stats.capsules.today} sub={`${stats.capsules.past_7_days} this week`} />
          <Metric label="Total ready" value={stats.capsules.ready} sub={`of ${stats.capsules.total}`} />
          <Metric label="Errors" value={stats.capsules.errors} sub={stats.capsules.generating ? `${stats.capsules.generating} in flight` : "all clear"} />
          <Metric
            label="Feedback"
            value={stats.feedback.total}
            sub={stats.feedback.avg_rating != null ? `★ ${stats.feedback.avg_rating} avg (${stats.feedback.rated_count})` : "no ratings"}
          />
        </section>

        <section className="mt-10 grid grid-cols-1 gap-8 md:grid-cols-2">
          <div>
            <h2 className="mb-4 font-mono text-[11px] uppercase tracking-[0.3em] text-zinc-500">By mode</h2>
            <div className="space-y-3 border border-zinc-200 bg-white p-6">
              {Object.entries(stats.by_mode).map(([m, n]) => (
                <Bar key={m} label={m} count={n} max={maxMode} />
              ))}
            </div>
          </div>
          <div>
            <h2 className="mb-4 font-mono text-[11px] uppercase tracking-[0.3em] text-zinc-500">By duration</h2>
            <div className="space-y-3 border border-zinc-200 bg-white p-6">
              {Object.entries(stats.by_duration).map(([d, n]) => (
                <Bar key={d} label={`${d} min`} count={n} max={maxDur} />
              ))}
            </div>
          </div>
        </section>

        {stats.top_tech_topics.length > 0 && (
          <section className="mt-10">
            <h2 className="mb-4 font-mono text-[11px] uppercase tracking-[0.3em] text-zinc-500">Top tech topics</h2>
            <div className="space-y-3 border border-zinc-200 bg-white p-6">
              {stats.top_tech_topics.map((t) => (
                <Bar key={t.topic} label={t.topic} count={t.count} max={maxTech} />
              ))}
            </div>
          </section>
        )}

        {stats.top_locations.length > 0 && (
          <section className="mt-10">
            <h2 className="mb-4 font-mono text-[11px] uppercase tracking-[0.3em] text-zinc-500">Top locations</h2>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              {stats.top_locations.map((l, i) => (
                <div key={`${l.city}-${i}`} className="flex items-center justify-between border border-zinc-200 bg-white p-4">
                  <span className="font-medium">{l.city || "—"}{l.country ? `, ${l.country}` : ""}</span>
                  <span className="font-mono text-sm tabular-nums text-zinc-500">{l.count}</span>
                </div>
              ))}
            </div>
          </section>
        )}

        <section className="mt-10">
          <h2 className="mb-4 font-mono text-[11px] uppercase tracking-[0.3em] text-zinc-500">
            Latest feedback ({feedback.length})
          </h2>
          {feedback.length === 0 ? (
            <p className="border border-zinc-200 bg-white p-6 text-sm text-zinc-500">
              No feedback yet — share the app around to get some.
            </p>
          ) : (
            <div className="space-y-3">
              {feedback.map((f) => (
                <div key={f.id} className="border border-zinc-200 bg-white p-5">
                  <div className="mb-2 flex items-center justify-between">
                    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                      {new Date(f.created_at).toLocaleString()}
                      {f.context && <span className="ml-2 text-zinc-400">· {f.context}</span>}
                    </div>
                    {f.rating != null && (
                      <div className="font-mono text-sm text-rose-600 tabular-nums">★ {f.rating}/5</div>
                    )}
                  </div>
                  <p className="whitespace-pre-wrap text-sm text-zinc-800">{f.message}</p>
                  {(f.email || f.capsule_id) && (
                    <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                      {f.email && <>from {f.email} · </>}
                      {f.capsule_id && <>capsule {f.capsule_id.slice(0, 8)}…</>}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
